import React, { useMemo } from 'react';
import { TrendingUp, TrendingDown, Star, Circle, Target } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface IterationData {
  iteration: number;
  objective_values: Record<string, number>;
  is_on_pareto: boolean;
  confidence?: number;
}

interface OptimizationProgressChartProps {
  iterations: IterationData[];
  objective: string;
  direction?: 'maximize' | 'minimize';
  target?: number;
  width?: number;
  height?: number;
  className?: string;
}

export function OptimizationProgressChart({
  iterations,
  objective,
  direction = 'maximize',
  target,
  width = 500,
  height = 280,
  className,
}: OptimizationProgressChartProps) {
  // Calculate chart data
  const { bounds, scale, points, bestPoint, trendLine, improvement, chartArea } = useMemo(() => {
    if (iterations.length === 0) {
      return {
        bounds: { minX: 0, maxX: 10, minY: 0, maxY: 1 },
        scale: { x: 1, y: 1 },
        points: [],
        bestPoint: null,
        trendLine: null,
        improvement: 0,
        chartArea: { left: 60, right: width - 20, top: 20, bottom: height - 45 },
      };
    }

    const values = iterations.map(it => it.objective_values[objective] ?? 0);
    const iterNums = iterations.map(it => it.iteration);

    const minY = Math.min(...values);
    const maxY = Math.max(...values);
    const yRange = maxY - minY || 0.1;
    const yPadding = yRange * 0.15;

    const minX = Math.min(...iterNums);
    const maxX = Math.max(...iterNums);
    const xRange = maxX - minX || 1;

    const bounds = {
      minX: minX - 0.5,
      maxX: maxX + 0.5,
      minY: minY - yPadding,
      maxY: maxY + yPadding,
    };

    // Adjust for target line if present
    if (target !== undefined) {
      bounds.minY = Math.min(bounds.minY, target - yPadding);
      bounds.maxY = Math.max(bounds.maxY, target + yPadding);
    }

    const chartArea = {
      left: 60,
      right: width - 20,
      top: 20,
      bottom: height - 45,
    };

    const chartWidth = chartArea.right - chartArea.left;
    const chartHeight = chartArea.bottom - chartArea.top;

    const scale = {
      x: chartWidth / (bounds.maxX - bounds.minX),
      y: chartHeight / (bounds.maxY - bounds.minY),
    };

    // Map iterations to points
    const points = iterations.map((it, idx) => {
      const value = it.objective_values[objective] ?? 0;
      return {
        x: it.iteration,
        y: value,
        iteration: it.iteration,
        isPareto: it.is_on_pareto,
        confidence: it.confidence,
        idx,
      };
    });

    // Find best point based on direction
    const bestIdx = direction === 'maximize'
      ? values.indexOf(Math.max(...values))
      : values.indexOf(Math.min(...values));
    const bestPoint = points[bestIdx] || null;

    // Calculate linear trend line
    let trendLine = null;
    if (points.length >= 2) {
      const n = points.length;
      const sumX = points.reduce((sum, p) => sum + p.x, 0);
      const sumY = points.reduce((sum, p) => sum + p.y, 0);
      const sumXY = points.reduce((sum, p) => sum + p.x * p.y, 0);
      const sumX2 = points.reduce((sum, p) => sum + p.x * p.x, 0);

      const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
      const intercept = (sumY - slope * sumX) / n;

      if (isFinite(slope) && isFinite(intercept)) {
        trendLine = { slope, intercept };
      }
    }

    // Calculate improvement from first to best
    const firstValue = values[0];
    const bestValue = direction === 'maximize' ? Math.max(...values) : Math.min(...values);
    const improvement = firstValue !== 0
      ? ((bestValue - firstValue) / Math.abs(firstValue)) * 100
      : bestValue * 100;

    return { bounds, scale, points, bestPoint, trendLine, improvement, chartArea };
  }, [iterations, objective, direction, target, width, height]);

  // Transform data point to SVG coordinates
  const toSvg = (x: number, y: number) => {
    const svgX = chartArea.left + (x - bounds.minX) * scale.x;
    const svgY = chartArea.bottom - (y - bounds.minY) * scale.y;
    return { x: svgX, y: svgY };
  };

  // Generate line path
  const linePath = useMemo(() => {
    if (points.length < 2) return '';
    const sorted = [...points].sort((a, b) => a.x - b.x);
    const pathPoints = sorted.map(p => {
      const svg = toSvg(p.x, p.y);
      return `${svg.x},${svg.y}`;
    });
    return `M ${pathPoints.join(' L ')}`;
  }, [points, bounds, scale, chartArea]);

  // Generate area fill path
  const areaPath = useMemo(() => {
    if (points.length < 2) return '';
    const sorted = [...points].sort((a, b) => a.x - b.x);
    const pathPoints = sorted.map(p => {
      const svg = toSvg(p.x, p.y);
      return `${svg.x},${svg.y}`;
    });
    const firstSvg = toSvg(sorted[0].x, sorted[0].y);
    const lastSvg = toSvg(sorted[sorted.length - 1].x, sorted[sorted.length - 1].y);
    return `M ${firstSvg.x},${chartArea.bottom} L ${pathPoints.join(' L ')} L ${lastSvg.x},${chartArea.bottom} Z`;
  }, [points, bounds, scale, chartArea]);

  // Generate trend line path
  const trendPath = useMemo(() => {
    if (!trendLine || points.length < 2) return '';
    const startX = bounds.minX;
    const endX = bounds.maxX;
    const startY = trendLine.slope * startX + trendLine.intercept;
    const endY = trendLine.slope * endX + trendLine.intercept;
    const start = toSvg(startX, Math.max(bounds.minY, Math.min(bounds.maxY, startY)));
    const end = toSvg(endX, Math.max(bounds.minY, Math.min(bounds.maxY, endY)));
    return `M ${start.x},${start.y} L ${end.x},${end.y}`;
  }, [trendLine, bounds, scale, chartArea]);

  // Format value for display
  const formatValue = (value: number) => {
    if (Math.abs(value) < 0.01) return value.toExponential(2);
    if (Math.abs(value) >= 1000) return value.toFixed(0);
    return value.toFixed(4);
  };

  // Generate axis ticks
  const xTicks = useMemo(() => {
    const allIters = points.map(p => p.iteration);
    if (allIters.length <= 10) return [...new Set(allIters)].sort((a, b) => a - b);
    const tickCount = 6;
    const range = bounds.maxX - bounds.minX;
    const step = Math.ceil(range / tickCount);
    return Array.from({ length: tickCount + 1 }, (_, i) => Math.round(bounds.minX + step * i))
      .filter(t => t >= Math.min(...allIters) && t <= Math.max(...allIters));
  }, [bounds, points]);

  const yTicks = useMemo(() => {
    const tickCount = 5;
    const range = bounds.maxY - bounds.minY;
    const step = range / tickCount;
    return Array.from({ length: tickCount + 1 }, (_, i) => bounds.minY + step * i);
  }, [bounds]);

  const isImproving = direction === 'maximize' ? improvement > 0 : improvement < 0;
  const TrendIcon = isImproving ? TrendingUp : TrendingDown;

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2">
            Optimization Progress
            {points.length >= 2 && (
              <Badge
                variant={isImproving ? "default" : "secondary"}
                className={cn("text-xs", isImproving ? "bg-green-600" : "bg-orange-500")}
              >
                <TrendIcon className="h-3 w-3 mr-1" />
                {Math.abs(improvement).toFixed(1)}%
              </Badge>
            )}
          </span>
          <div className="flex gap-2">
            <Badge variant="outline" className="text-xs">
              <Circle className="h-2 w-2 mr-1 fill-blue-500 text-blue-500" />
              Progress
            </Badge>
            <Badge variant="outline" className="text-xs text-amber-600">
              <Star className="h-2 w-2 mr-1 fill-amber-500" />
              Best
            </Badge>
            {target !== undefined && (
              <Badge variant="outline" className="text-xs text-green-600">
                <Target className="h-2 w-2 mr-1" />
                Target
              </Badge>
            )}
          </div>
        </CardTitle>
        <CardDescription>
          {objective} ({direction}) over {points.length} iteration{points.length !== 1 ? 's' : ''}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <TooltipProvider>
          <svg
            width={width}
            height={height}
            className="font-mono text-xs"
            viewBox={`0 0 ${width} ${height}`}
          >
            {/* Gradient fill */}
            <defs>
              <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgb(59, 130, 246)" stopOpacity="0.3" />
                <stop offset="100%" stopColor="rgb(59, 130, 246)" stopOpacity="0.05" />
              </linearGradient>
            </defs>

            {/* Grid lines */}
            <g className="text-muted stroke-muted">
              {xTicks.map((tick, i) => {
                const svg = toSvg(tick, bounds.minY);
                return (
                  <line
                    key={`x-grid-${i}`}
                    x1={svg.x}
                    y1={chartArea.bottom}
                    x2={svg.x}
                    y2={chartArea.top}
                    strokeDasharray="2,2"
                    strokeOpacity={0.3}
                  />
                );
              })}
              {yTicks.map((tick, i) => {
                const svg = toSvg(bounds.minX, tick);
                return (
                  <line
                    key={`y-grid-${i}`}
                    x1={chartArea.left}
                    y1={svg.y}
                    x2={chartArea.right}
                    y2={svg.y}
                    strokeDasharray="2,2"
                    strokeOpacity={0.3}
                  />
                );
              })}
            </g>

            {/* Target line */}
            {target !== undefined && (
              <g>
                <line
                  x1={chartArea.left}
                  y1={toSvg(0, target).y}
                  x2={chartArea.right}
                  y2={toSvg(0, target).y}
                  className="stroke-green-500"
                  strokeWidth={2}
                  strokeDasharray="8,4"
                />
                <text
                  x={chartArea.right - 5}
                  y={toSvg(0, target).y - 5}
                  textAnchor="end"
                  className="fill-green-600"
                  fontSize={10}
                >
                  Target: {formatValue(target)}
                </text>
              </g>
            )}

            {/* Trend line */}
            {trendPath && (
              <path
                d={trendPath}
                fill="none"
                className={cn(
                  "stroke-2",
                  isImproving ? "stroke-green-400" : "stroke-orange-400"
                )}
                strokeDasharray="6,3"
                strokeOpacity={0.6}
              />
            )}

            {/* Area fill */}
            {areaPath && (
              <path
                d={areaPath}
                fill="url(#areaGradient)"
              />
            )}

            {/* Main line */}
            {linePath && (
              <path
                d={linePath}
                fill="none"
                className="stroke-blue-500"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {/* X Axis */}
            <line
              x1={chartArea.left}
              y1={chartArea.bottom}
              x2={chartArea.right}
              y2={chartArea.bottom}
              className="stroke-foreground"
            />
            {xTicks.map((tick, i) => {
              const svg = toSvg(tick, bounds.minY);
              return (
                <g key={`x-tick-${i}`}>
                  <line
                    x1={svg.x}
                    y1={chartArea.bottom}
                    x2={svg.x}
                    y2={chartArea.bottom + 5}
                    className="stroke-foreground"
                  />
                  <text
                    x={svg.x}
                    y={chartArea.bottom + 16}
                    textAnchor="middle"
                    className="fill-muted-foreground"
                    fontSize={10}
                  >
                    {tick}
                  </text>
                </g>
              );
            })}
            <text
              x={(chartArea.left + chartArea.right) / 2}
              y={height - 5}
              textAnchor="middle"
              className="fill-foreground"
              fontSize={11}
            >
              Iteration
            </text>

            {/* Y Axis */}
            <line
              x1={chartArea.left}
              y1={chartArea.top}
              x2={chartArea.left}
              y2={chartArea.bottom}
              className="stroke-foreground"
            />
            {yTicks.map((tick, i) => {
              const svg = toSvg(bounds.minX, tick);
              return (
                <g key={`y-tick-${i}`}>
                  <line
                    x1={chartArea.left - 5}
                    y1={svg.y}
                    x2={chartArea.left}
                    y2={svg.y}
                    className="stroke-foreground"
                  />
                  <text
                    x={chartArea.left - 8}
                    y={svg.y + 3}
                    textAnchor="end"
                    className="fill-muted-foreground"
                    fontSize={10}
                  >
                    {formatValue(tick)}
                  </text>
                </g>
              );
            })}
            <text
              x={15}
              y={(chartArea.top + chartArea.bottom) / 2}
              textAnchor="middle"
              className="fill-foreground"
              fontSize={11}
              transform={`rotate(-90, 15, ${(chartArea.top + chartArea.bottom) / 2})`}
            >
              {objective}
            </text>

            {/* Data points */}
            {points.map((point, i) => {
              const svg = toSvg(point.x, point.y);
              const isBest = bestPoint && point.idx === bestPoint.idx;

              return (
                <Tooltip key={`point-${i}`}>
                  <TooltipTrigger asChild>
                    <g className="cursor-pointer">
                      {isBest ? (
                        <>
                          {/* Glow effect for best */}
                          <circle
                            cx={svg.x}
                            cy={svg.y}
                            r={12}
                            className="fill-amber-400/20"
                          />
                          <circle
                            cx={svg.x}
                            cy={svg.y}
                            r={8}
                            className="fill-amber-400 stroke-amber-600"
                            strokeWidth={2}
                          />
                          <text
                            x={svg.x}
                            y={svg.y + 4}
                            textAnchor="middle"
                            className="fill-amber-900 pointer-events-none"
                            fontSize={10}
                          >
                            ★
                          </text>
                        </>
                      ) : (
                        <circle
                          cx={svg.x}
                          cy={svg.y}
                          r={5}
                          className={cn(
                            "stroke-2",
                            point.isPareto
                              ? "fill-blue-500 stroke-blue-700"
                              : "fill-blue-400 stroke-blue-500"
                          )}
                        />
                      )}
                    </g>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-xs space-y-1">
                      <p className="font-medium">
                        {isBest && <Star className="h-3 w-3 inline mr-1 text-amber-500" />}
                        Iteration {point.iteration}
                      </p>
                      <p className="font-mono">{objective}: {formatValue(point.y)}</p>
                      {point.confidence !== undefined && (
                        <p className="text-muted-foreground">
                          Confidence: {(point.confidence * 100).toFixed(0)}%
                        </p>
                      )}
                      {point.isPareto && !isBest && (
                        <p className="text-blue-600">On Pareto Frontier</p>
                      )}
                      {isBest && (
                        <p className="text-amber-600">Best Solution</p>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </svg>
        </TooltipProvider>

        {/* Stats bar */}
        <div className="flex justify-between text-xs text-muted-foreground mt-2 pt-2 border-t">
          <span>
            Best: <span className="font-mono text-foreground">{bestPoint ? formatValue(bestPoint.y) : 'N/A'}</span>
          </span>
          {points.length >= 2 && (
            <span>
              Start: <span className="font-mono">{formatValue(points[0].y)}</span>
              {' → '}
              Latest: <span className="font-mono">{formatValue(points[points.length - 1].y)}</span>
            </span>
          )}
          {target !== undefined && bestPoint && (
            <span>
              {direction === 'maximize'
                ? (bestPoint.y >= target ? '✓ Target reached' : `${((target - bestPoint.y) / target * 100).toFixed(1)}% to target`)
                : (bestPoint.y <= target ? '✓ Target reached' : `${((bestPoint.y - target) / target * 100).toFixed(1)}% to target`)
              }
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default OptimizationProgressChart;
